/**
 * Las cuentas del editor de recetas, sin pantalla.
 *
 * Son las mismas reglas que `recipe_line.dart` en el POS: los dos editores
 * tienen que aceptar lo mismo para la misma receta. Viven aparte porque son
 * las que pueden estropear el almacén en silencio: un cocinero que escribe
 * `200` pensando en gramos, en un insumo que se cuenta en kilos, descontaba
 * 200 kg por plato y nada lo decía.
 */

/**
 * Una unidad en la que se puede escribir una cantidad de receta.
 *
 * `milesimasPorUnidad` es cuántas milésimas de la unidad del almacén vale una
 * de esta: un gramo es una milésima de kilo, un kilo son mil.
 */
export interface UnidadDeCocina {
  codigo: string;
  milesimasPorUnidad: number;
}

/**
 * Las unidades de cocina para un insumo que se cuenta en `unidadDeStock`. La
 * primera es la que se ofrece al agregarlo: un kilo de lomo se pesa en gramos.
 */
export function unidadesDeCocina(unidadDeStock: string): UnidadDeCocina[] {
  switch (unidadDeStock.trim().toLowerCase()) {
    case 'kg':
      return [
        { codigo: 'g', milesimasPorUnidad: 1 },
        { codigo: unidadDeStock, milesimasPorUnidad: 1000 },
      ];
    case 'l':
    case 'lt':
      return [
        { codigo: 'ml', milesimasPorUnidad: 1 },
        { codigo: unidadDeStock, milesimasPorUnidad: 1000 },
      ];
    default:
      return [{ codigo: unidadDeStock, milesimasPorUnidad: 1000 }];
  }
}

/**
 * Lo escrito, en milésimas de la unidad del almacén. `null` si no se puede
 * guardar: vacío, cero, más de tres decimales o medio gramo —la milésima de
 * kilo es lo más fino que guarda el almacén—.
 */
export function aMilesimas(escrito: string, unidad: UnidadDeCocina): number | null {
  const limpio = escrito.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d{1,9}(\.\d{1,3})?$/.test(limpio)) return null;

  const [entero, decimales = ''] = limpio.split('.');
  const escritoEnMilesimas = Number(entero) * 1000 + Number(decimales.padEnd(3, '0'));

  if ((escritoEnMilesimas * unidad.milesimasPorUnidad) % 1000 !== 0) return null;

  const resultado = (escritoEnMilesimas * unidad.milesimasPorUnidad) / 1000;
  return resultado <= 0 ? null : resultado;
}

/** Milésimas como viajan a la API: `200` → `"0.200"`. */
export function textoDeStock(milesimas: number): string {
  const entero = Math.trunc(milesimas / 1000);
  const decimales = String(milesimas % 1000).padStart(3, '0');
  return `${entero}.${decimales}`;
}

/** La cantidad que viene del almacén (`"0.250"`), en milésimas. */
export function milesimasDeStock(cantidad: string): number {
  return aMilesimas(cantidad, { codigo: '', milesimasPorUnidad: 1000 }) ?? 0;
}

/** Una cantidad del almacén escrita en `unidad`, sin ceros de más. */
export function enUnidad(milesimas: number, unidad: UnidadDeCocina): string {
  const escrito = (milesimas * 1000) / unidad.milesimasPorUnidad;
  const entero = Math.trunc(escrito / 1000);
  const fraccion = escrito % 1000;
  if (fraccion === 0) return String(entero);

  return `${entero},${String(fraccion).padStart(3, '0').replace(/0+$/, '')}`;
}

/** Con qué unidad se abre una línea guardada: menos de un kilo, en gramos. */
export function unidadPreferida(unidadDeStock: string, milesimas: number): UnidadDeCocina {
  const unidades = unidadesDeCocina(unidadDeStock);
  if (unidades.length === 1) return unidades[0]!;
  return milesimas < 1000 ? unidades[0]! : unidades[unidades.length - 1]!;
}

/**
 * El costo por unidad del almacén (`"48.0000"`) en diezmilésimas de sol, como
 * entero: lo que cuesta cada línea no pasa por coma flotante.
 */
export function costoUnitario(costoPromedio: string): number {
  const m = /^(\d+)(?:\.(\d+))?$/.exec(costoPromedio.trim());
  if (m === null) return 0;
  return Number(m[1]) * 10000 + Number((m[2] ?? '').padEnd(4, '0').slice(0, 4));
}

/** Lo que cuesta una línea, en céntimos. */
export function costoDeLinea(milesimas: number, costoEnDiezmilesimas: number): number {
  // milésimas × diezmilésimas = 10⁻⁷ soles; al céntimo, entre 10⁵.
  return Math.floor((milesimas * costoEnDiezmilesimas + 50000) / 100000);
}

/**
 * Lo que cuesta el plato en céntimos, con la merma dentro: la misma cuenta que
 * `recipe-cost.ts` del servidor.
 */
export function costoDeReceta(lineas: number[], merma: number): number {
  const insumos = lineas.reduce((total, linea) => total + linea, 0);
  return Math.round((insumos * (100 + merma)) / 100);
}

/** Qué parte del precio se va en insumos, en porcentaje. Cero sin precio. */
export function foodCost(costoEnCentimos: number, precioEnCentimos: number): number {
  return precioEnCentimos <= 0 ? 0 : (costoEnCentimos * 100) / precioEnCentimos;
}

/** Cuántos platos salen con lo que hay, y qué se acaba primero. */
export function porcionesPosibles(
  lineas: { nombre: string; necesita: number; hay: number }[],
): { porciones: number; limita: string | null } {
  let menor = -1;
  let limita: string | null = null;

  for (const linea of lineas) {
    if (linea.necesita <= 0) continue;
    const salen = Math.floor(Math.max(0, linea.hay) / linea.necesita);
    if (menor === -1 || salen < menor) {
      menor = salen;
      limita = linea.nombre;
    }
  }

  return { porciones: Math.max(0, menor), limita: menor === -1 ? null : limita };
}

/** Más que esto por porción, en kilos o litros, no es una porción: es un error. */
export const MILESIMAS_SOSPECHOSAS = 2000;

/**
 * Si una línea es desproporcionada para una porción, en qué unidad se quiso
 * escribir probablemente. `undefined` si se ve razonable; `null` si hay que
 * revisarla pero no hay unidad que sugerir.
 */
export function avisoDeLinea(
  unidadDeStock: string,
  unidad: UnidadDeCocina,
  milesimas: number,
): UnidadDeCocina | null | undefined {
  const unidades = unidadesDeCocina(unidadDeStock);
  if (unidades.length === 1 || milesimas <= MILESIMAS_SOSPECHOSAS) return undefined;

  const menor = unidades[0]!;
  return unidad.codigo === menor.codigo ? null : menor;
}

/** Minúsculas y sin tildes: «Ají» y «aji» son la misma búsqueda. */
export function plegar(texto: string): string {
  return texto.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * Los nombres que se parecen a lo buscado sin contenerlo: lo que evita crear
 * «Culantro» cuando ya existe «Cilantro fresco». Palabra por palabra.
 */
export function parecidos<T>(buscado: string, candidatos: T[], nombreDe: (c: T) => string, limite = 3): T[] {
  const q = plegar(buscado);
  if (q.length < 4) return [];

  const tolerancia = q.length <= 5 ? 1 : 2;
  return candidatos
    .map((candidato) => {
      const nombre = plegar(nombreDe(candidato));
      if (nombre.includes(q)) return null;
      const mejor = Math.min(
        ...nombre.split(/\s+/).filter((p) => p.length >= 3).map((p) => distancia(q, p)),
        Number.POSITIVE_INFINITY,
      );
      return mejor <= tolerancia ? { candidato, mejor } : null;
    })
    .filter((x): x is { candidato: T; mejor: number } => x !== null)
    .sort((a, b) => a.mejor - b.mejor)
    .slice(0, limite)
    .map((x) => x.candidato);
}

function distancia(a: string, b: string): number {
  let previa = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const actual = [i];
    for (let j = 1; j <= b.length; j++) {
      actual[j] = Math.min(
        actual[j - 1]! + 1,
        previa[j]! + 1,
        previa[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    previa = actual;
  }
  return previa[b.length]!;
}
